import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Undo2 } from 'lucide-react';
import SimaSignModal, { type SimaSignResult } from '../components/SimaSignModal';
import DocumentPageLayout from '../components/document/DocumentPageLayout';
import { useIsDesktop } from '../hooks/useIsDesktop';
import {
  findSignerGuidForUser,
  getPartySignerGuid,
  parseSignerType,
  parseSigningPartyRegistryStatus,
  SIGNER_TYPE_SIGNER,
  SIGNER_TYPE_VISA,
  type DocumentMobileTab,
} from '../lib/documentHelpers';

const SIMA_POLL_INTERVAL_MS = 2500;
const SIMA_POLL_MAX_MS = 5 * 60 * 1000;
const SIMA_THANKS_CLOSE_MS = 2000;

const Document: React.FC = () => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const docId = searchParams.get('did') ?? '';
  const signerId = searchParams.get('sid') ?? '';
  const { token, user, isLoading: authLoading } = useAuth();
  const isDesktop = useIsDesktop();

  const [documentInfo, setDocumentInfo] = useState<any | null>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [statusConfirm, setStatusConfirm] = useState<'release' | 'reject' | null>(null);
  const [isUpdatingRegistryStatus, setIsUpdatingRegistryStatus] = useState(false);
  const [openSimaSignModal, setOpenSimaSignModal] = useState(false);
  const [simaSignResult, setSimaSignResult] = useState<SimaSignResult | null>(null);
  const [qrCodeValue, setQrCodeValue] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DocumentMobileTab>('details');

  const currentSignerParty = useMemo(() => {
    const sid = signerId.trim();
    const parties = documentInfo?.SignParties ?? documentInfo?.signParties;
    if (!Array.isArray(parties) || parties.length === 0 || !sid) return undefined;
    return parties.find((p: any) => {
      const pid = getPartySignerGuid(p);
      return pid.length > 0 && pid === sid;
    });
  }, [documentInfo?.SignParties, documentInfo?.signParties, signerId]);

  const currentSignerType = currentSignerParty ? parseSignerType(currentSignerParty) : null;
  const currentPartyStatus = currentSignerParty
    ? parseSigningPartyRegistryStatus(currentSignerParty)
    : undefined;
  const currentPartyDone =
    currentPartyStatus === 2 ||
    currentPartyStatus === 1 ||
    currentPartyStatus === -1 ||
    currentPartyStatus === -2;
  const showSignWithSima = currentSignerType !== SIGNER_TYPE_VISA && !currentPartyDone;
  const showRelease = currentSignerType !== SIGNER_TYPE_SIGNER && !currentPartyDone;

  const revokePdfUrl = useCallback((url: string | null) => {
    if (url) URL.revokeObjectURL(url);
  }, []);

  const handleGetDocument = useCallback(async (docGuid?: string) => {
    const guid = docGuid || documentInfo?.docGuid || docId;
    if (!guid) {
      setError(t('documentGUIDNotFound'));
      return;
    }

    setIsLoadingPdf(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_SIGNER_API_URL}/api/Document/GetFile/${guid}`, {
        method: 'GET',
        headers: {
          accept: '*/*',
          'accept-language': 'en-GB,en-US;q=0.9,en;q=0.8,ru;q=0.7,az;q=0.6',
        },
      });

      if (!response.ok) {
        throw new Error(t('failedToFetchDocument'));
      }

      const data = await response.json();

      let base64Data: string;
      if (data.dataObjects && data.dataObjects[0] && data.dataObjects[0].data) {
        base64Data = data.dataObjects[0].data;
      } else if (data.data) {
        base64Data = data.data;
      } else {
        throw new Error(t('invalidResponseFormat'));
      }

      const blob = new Blob(
        [Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0))],
        { type: 'application/pdf' },
      );
      const url = URL.createObjectURL(blob);
      setPdfBlob(blob);
      setPdfUrl((prev) => {
        revokePdfUrl(prev);
        return url;
      });
    } catch (fetchError) {
      console.error('Error fetching document:', fetchError);
      setError(t('errorFetchingDocument'));
    } finally {
      setIsLoadingPdf(false);
    }
  }, [documentInfo?.docGuid, docId, revokePdfUrl, t]);

  const handleGetDocumentRef = useRef(handleGetDocument);
  handleGetDocumentRef.current = handleGetDocument;

  const closeSimaSignModal = useCallback(() => {
    setOpenSimaSignModal(false);
    setSimaSignResult(null);
  }, []);

  useEffect(() => {
    const fetchDocumentInfo = async () => {
      if (!docId && !signerId) return;
      if (authLoading) return;

      setError(null);

      try {
        const queryParams: Record<string, string> = {};
        if (docId.trim()) queryParams.docId = docId;
        if (signerId.trim()) queryParams.signerId = signerId;
        if (Object.keys(queryParams).length === 0) return;

        const query = new URLSearchParams(queryParams).toString();
        const headers: HeadersInit = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(`${process.env.REACT_APP_SIGNER_API_URL}/api/Document/GetDocument?${query}`, {
          headers,
        });

        if (response.status === 401 || response.status === 403) {
          setError(t('signingLinkAccessDenied'));
          return;
        }

        if (!response.ok) {
          throw new Error(t('failedToFetchDocumentInfo'));
        }

        const data = await response.json();
        setDocumentInfo(data);

        const resolvedSid = user
          ? findSignerGuidForUser(data.SignParties, user.fin, user.email)
          : undefined;
        const currentSid = signerId.trim();
        const shouldUpdateSid =
          docId.trim().length > 0 &&
          !!user &&
          !!resolvedSid &&
          (!currentSid || currentSid !== resolvedSid);
        if (shouldUpdateSid && resolvedSid) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.set('did', docId.trim());
              next.set('sid', resolvedSid);
              return next;
            },
            { replace: true },
          );
        }
      } catch (fetchError) {
        console.error('Error fetching document information:', fetchError);
        setError(t('errorFetchingDocumentInfo'));
      }
    };

    fetchDocumentInfo();
  }, [docId, signerId, token, authLoading, user, setSearchParams, t]);

  useEffect(() => {
    setPdfBlob(null);
    setPdfUrl((prev) => {
      revokePdfUrl(prev);
      return null;
    });
  }, [documentInfo?.docGuid, revokePdfUrl]);

  useEffect(() => {
    if (!documentInfo?.docGuid) return;
    void handleGetDocument(documentInfo.docGuid);
  }, [documentInfo?.docGuid, handleGetDocument]);

  useEffect(() => {
    return () => {
      revokePdfUrl(pdfUrl);
    };
  }, [pdfUrl, revokePdfUrl]);

  const handleSignWithSima = async () => { 
    try {
      const response = await fetch(`${process.env.REACT_APP_SIGNER_API_URL}/api/SignProcessor/simaqr`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          docId: documentInfo.docGuid,
          signerId: signerId,
          providerName: 'Sima',
        }),
      });

      if (!response.ok) {
        throw new Error(t('failedToInitiateSigning'));
      }

      const data = await response.json();

      if (data.isDocumentLocked) {
        setError(t('documentSigningByOther'));
        return;
      }

      setSimaSignResult('waiting');
      setOpenSimaSignModal(true);
      setQrCodeValue(data.qrText);
      setError(null);
    } catch (signError) {
      console.error('Error initiating signing process:', signError);
      setError(t('errorInitiatingSigning'));
    }
  };

  useEffect(() => {
    if (!openSimaSignModal || simaSignResult !== 'waiting') return;

    const sid = signerId.trim();
    if (!sid) return;

    let stopped = false;
    let inFlight = false;
    const startedAt = Date.now();

    const tick = async () => {
      if (stopped || inFlight) return;
      if (Date.now() - startedAt > SIMA_POLL_MAX_MS) return;

      inFlight = true;
      try {
        const queryParams: Record<string, string> = { signerId: sid };
        if (docId.trim()) queryParams.docId = docId.trim();
        const query = new URLSearchParams(queryParams).toString();
        const headers: HeadersInit = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(
          `${process.env.REACT_APP_SIGNER_API_URL}/api/Document/GetDocument?${query}`,
          { headers },
        );
        if (!response.ok || stopped) return;

        const data = await response.json();
        const parties = data.SignParties ?? data.signParties;
        if (!Array.isArray(parties) || parties.length === 0) {
          stopped = true;
          setError(t('errorFetchingDocumentInfo'));
          return;
        }

        setDocumentInfo(data);

        const current = parties.find((party: unknown) => getPartySignerGuid(party as { signerGUID?: unknown; SignerGUID?: unknown }) === sid);
        const status = parseSigningPartyRegistryStatus(current ?? {});

        if (status === 2) {
          stopped = true;
          setSimaSignResult('signed');
          void handleGetDocumentRef.current(data.docGuid);
          return;
        }
        if (status === -1) {
          stopped = true;
          setSimaSignResult('rejected');
          return;
        }
        if (status === -2) {
          stopped = true;
          setSimaSignResult('expired');
        }
      } catch (pollError) {
        console.error('Error polling signing status:', pollError);
      } finally {
        inFlight = false;
      }
    };

    void tick();
    const id = setInterval(tick, SIMA_POLL_INTERVAL_MS);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [openSimaSignModal, simaSignResult, signerId, docId, token, t]);

  useEffect(() => {
    if (!openSimaSignModal || simaSignResult !== 'signed') return;
    const id = setTimeout(() => {
      closeSimaSignModal();
    }, SIMA_THANKS_CLOSE_MS);
    return () => clearTimeout(id);
  }, [openSimaSignModal, simaSignResult, closeSimaSignModal]);

  const handleConfirmRegistryStatus = async () => {
    if (!statusConfirm) return;
    const sid = signerId.trim();
    if (!sid) {
      setError(t('documentNotReadyNoSignerId'));
      setStatusConfirm(null);
      return;
    }

    const signStatus = statusConfirm === 'release' ? 1 : -1;
    setIsUpdatingRegistryStatus(true);
    setError(null);

    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${process.env.REACT_APP_SIGNER_API_URL}/api/Document/UpdateStatus`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ signerId: sid, signStatus }),
      });

      if (!response.ok) {
        throw new Error(t('failedToUpdateSignStatus'));
      }

      setStatusConfirm(null);
      window.location.reload();
    } catch (err) {
      console.error('Error updating sign registry status:', err);
      setError(err instanceof Error ? err.message : t('failedToUpdateSignStatus'));
    } finally {
      setIsUpdatingRegistryStatus(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <DocumentPageLayout
        isDesktop={isDesktop}
        isAppSidebarOpen={isDesktop}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        documentInfo={documentInfo}
        signerId={signerId}
        authLoading={authLoading}
        hasQueryParams={!!(docId || signerId)}
        pdfBlob={pdfBlob}
        pdfUrl={pdfUrl}
        isLoadingPdf={isLoadingPdf}
        pageError={error}
        onLoadDocument={() => void handleGetDocument()}
        showSignWithSima={showSignWithSima}
        showRelease={showRelease}
        isUpdatingRegistryStatus={isUpdatingRegistryStatus}
        onSignWithSima={() => void handleSignWithSima()}
        onRelease={() => setStatusConfirm('release')}
        onReject={() => setStatusConfirm('reject')}
      />

      <Dialog
        open={statusConfirm !== null}
        onOpenChange={(v) => {
          if (!v) setStatusConfirm(null);
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {statusConfirm === 'release' ? t('release') : statusConfirm === 'reject' ? t('reject') : ''}
            </DialogTitle>
            {statusConfirm === 'release' && (
              <DialogDescription className="text-left">{t('releaseDialogMessage')}</DialogDescription>
            )}
            {statusConfirm === 'reject' && (
              <DialogDescription className="text-left">{t('rejectDialogMessage')}</DialogDescription>
            )}
          </DialogHeader>
          <DialogFooter className="mt-4 gap-2 sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setStatusConfirm(null)} disabled={isUpdatingRegistryStatus}>
              <Undo2 className="h-4 w-4" />
              {t('cancel')}
            </Button>
            <Button
              type="button"
              variant={statusConfirm === 'reject' ? 'destructive' : 'default'}
              className={
                statusConfirm === 'release'
                  ? 'bg-yellow-400 text-yellow-950 hover:bg-yellow-500 dark:bg-yellow-500 dark:text-yellow-950 dark:hover:bg-yellow-600'
                  : undefined
              }
              onClick={handleConfirmRegistryStatus}
              disabled={isUpdatingRegistryStatus || statusConfirm === null}
            >
              {statusConfirm === 'release' ? t('releaseDialogConfirm') : statusConfirm === 'reject' ? t('rejectDialogConfirm') : t('confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SimaSignModal
        open={openSimaSignModal}
        onClose={closeSimaSignModal}
        qrCodeValue={qrCodeValue || ''}
        result={simaSignResult}
      />
    </div>
  );
};

export default Document;
